/**
 * AllowedTrafficPage — per-campaign traffic-source whitelist editor.
 *
 * Layout:
 *   1. Header with GREEN accent headline.
 *   2. Rule editor card: campaign-id input + traffic-source multi-picker
 *      (checkbox grid of canonical channel codes) + save button.
 *   3. Existing rules card: list of campaigns with channel badges + status
 *      badge ("Active" when non-empty, "Empty" otherwise) + edit action.
 *
 * Palette is limited to zinc (neutrals) + green (accent) + red (error).
 * No emerald, blue, orange, purple, dark, emoji, or competitor references.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useAllowedTrafficList,
	useAllowedTrafficUpsert,
} from "./hooks";
import type { AllowedTrafficView, ChannelCode } from "./api";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const BTN_PRIMARY =
	"rounded-[6px] border border-green-700 bg-green-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50";
const CHANNEL_LABEL: Record<ChannelCode, string> = {
	display: "Display",
	email: "Email",
	incentive: "Incentive",
	influencer: "Influencer",
	native: "Native",
	push: "Push",
	search: "Search",
	seo: "SEO",
	social: "Social",
	video: "Video",
};

export function AllowedTrafficPage() {
	const listQuery = useAllowedTrafficList();
	const upsertMut = useAllowedTrafficUpsert();

	const allowedChannels: ChannelCode[] = useMemo(
		() => listQuery.data?.allowed_channels ?? [],
		[listQuery.data],
	);

	const [campaignId, setCampaignId] = useState("");
	const [picked, setPicked] = useState<Set<ChannelCode>>(() => new Set());

	const togglePicked = (code: ChannelCode) => {
		setPicked((prev) => {
			const next = new Set(prev);
			if (next.has(code)) {
				next.delete(code);
			} else {
				next.add(code);
			}
			return next;
		});
	};

	const loadExisting = (row: AllowedTrafficView) => {
		setCampaignId(row.campaign_id);
		setPicked(new Set(row.channels));
	};

	const resetForm = () => {
		setCampaignId("");
		setPicked(new Set());
	};

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		const trimmed = campaignId.trim();
		if (!trimmed || picked.size === 0) return;
		upsertMut.mutate(
			{ campaignId: trimmed, body: { channels: Array.from(picked).sort() } },
			{ onSuccess: () => resetForm() },
		);
	};

	const canSave =
		campaignId.trim().length > 0 &&
		picked.size > 0 &&
		!upsertMut.isPending;

	const campaigns: AllowedTrafficView[] = listQuery.data?.campaigns ?? [];

	return (
		<main
			data-testid="allowed-traffic-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					Allowed traffic
				</p>
				<h1 className="text-3xl font-semibold text-green-700">
					Traffic source whitelist
				</h1>
				<p className="max-w-2xl text-sm text-zinc-600">
					Restrict a campaign to a curated set of traffic channels. Unknown
					or unlisted sources will be rejected by the network gateway.
				</p>
			</header>

			<section className={CARD} aria-label="Rule editor">
				<h2 className="text-base font-semibold text-zinc-900">
					Edit campaign rule
				</h2>
				<form
					onSubmit={handleSubmit}
					className="mt-4 flex flex-col gap-5"
					data-testid="allowed-traffic-rule-form"
				>
					<label className="flex flex-col gap-1">
						<span className="text-xs font-medium text-zinc-700">
							Campaign ID
						</span>
						<input
							type="text"
							className={INPUT}
							placeholder="e.g. summer-social-2026"
							value={campaignId}
							onChange={(e) => setCampaignId(e.currentTarget.value)}
							disabled={upsertMut.isPending}
							data-testid="allowed-traffic-campaign-input"
							autoComplete="off"
						/>
					</label>

					<fieldset
						className="flex flex-col gap-2"
						data-testid="allowed-traffic-sources"
					>
						<legend className="text-xs font-medium text-zinc-700">
							Traffic sources
						</legend>
						{allowedChannels.length === 0 ? (
							<p className="text-xs text-zinc-500">
								Loading channel catalog.
							</p>
						) : (
							<div
								className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-5"
								role="group"
								aria-label="Traffic source picker"
							>
								{allowedChannels.map((code) => {
									const on = picked.has(code);
									return (
										<label
											key={code}
											className={
												on
													? "flex cursor-pointer items-center gap-2 rounded-[6px] border border-green-700 bg-green-50 px-3 py-2 text-sm text-green-800"
													: "flex cursor-pointer items-center gap-2 rounded-[6px] border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-700 hover:border-zinc-300"
											}
											data-testid={`allowed-traffic-source-${code}`}
										>
											<input
												type="checkbox"
												className="h-4 w-4 accent-green-700"
												checked={on}
												onChange={() => togglePicked(code)}
												disabled={upsertMut.isPending}
												data-testid={`allowed-traffic-source-input-${code}`}
											/>
											<span>{CHANNEL_LABEL[code]}</span>
										</label>
									);
								})}
							</div>
						)}
					</fieldset>

					<div className="flex flex-wrap items-center gap-2">
						<button
							type="submit"
							className={BTN_PRIMARY}
							disabled={!canSave}
							data-testid="allowed-traffic-save"
						>
							{upsertMut.isPending ? "Saving" : "Save rule"}
						</button>
						<button
							type="button"
							className={BTN_GHOST}
							onClick={resetForm}
							disabled={upsertMut.isPending}
							data-testid="allowed-traffic-reset"
						>
							Reset
						</button>
						<span
							className="text-xs text-zinc-500"
							data-testid="allowed-traffic-picked-count"
						>
							{picked.size} selected
						</span>
					</div>

					{upsertMut.error ? (
						<p
							className="text-xs text-red-600"
							role="alert"
							data-testid="allowed-traffic-save-error"
						>
							{upsertMut.error.message}
						</p>
					) : null}
				</form>
			</section>

			<section className={CARD} aria-label="Existing rules">
				<div className="flex items-center justify-between">
					<h2 className="text-base font-semibold text-zinc-900">
						Existing rules
					</h2>
					<span
						className="text-xs text-zinc-500"
						data-testid="allowed-traffic-total"
					>
						{campaigns.length} campaign{campaigns.length === 1 ? "" : "s"}
					</span>
				</div>

				{listQuery.isLoading ? (
					<p
						className="mt-4 text-sm text-zinc-500"
						data-testid="allowed-traffic-loading"
					>
						Loading rules
					</p>
				) : listQuery.error ? (
					<p
						className="mt-4 text-sm text-red-600"
						role="alert"
						data-testid="allowed-traffic-list-error"
					>
						{listQuery.error.message}
					</p>
				) : campaigns.length === 0 ? (
					<p
						className="mt-4 text-sm text-zinc-500"
						data-testid="allowed-traffic-empty"
					>
						No campaign rules yet. Create one above to start enforcing the
						whitelist.
					</p>
				) : (
					<ul
						className="mt-4 divide-y divide-zinc-200"
						data-testid="allowed-traffic-list"
					>
						{campaigns.map((row) => {
							const active = row.channels.length > 0;
							return (
								<li
									key={row.campaign_id}
									className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start sm:justify-between"
									data-testid={`allowed-traffic-row-${row.campaign_id}`}
								>
									<div className="flex flex-col gap-1">
										<div className="flex items-center gap-2">
											<span className="font-mono text-sm text-zinc-900">
												{row.campaign_id}
											</span>
											<span
												className={
													active
														? "rounded-full border border-green-700 bg-green-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-green-800"
														: "rounded-full border border-zinc-300 bg-zinc-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-600"
												}
												data-testid={`allowed-traffic-status-${row.campaign_id}`}
											>
												{active ? "Active" : "Empty"}
											</span>
										</div>
										<div
											className="flex flex-wrap gap-1"
											data-testid={`allowed-traffic-badges-${row.campaign_id}`}
										>
											{row.channels.length === 0 ? (
												<span className="text-xs text-zinc-500">
													No channels
												</span>
											) : (
												row.channels.map((c) => (
													<span
														key={c}
														className="rounded-[4px] border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-[11px] font-medium text-zinc-700"
														data-testid={`allowed-traffic-badge-${row.campaign_id}-${c}`}
													>
														{CHANNEL_LABEL[c]}
													</span>
												))
											)}
										</div>
									</div>
									<button
										type="button"
										className={BTN_GHOST}
										onClick={() => loadExisting(row)}
										disabled={upsertMut.isPending}
										data-testid={`allowed-traffic-edit-${row.campaign_id}`}
									>
										Edit
									</button>
								</li>
							);
						})}
					</ul>
				)}
			</section>
		</main>
	);
}

export default AllowedTrafficPage;
